import clsx from 'clsx';
import { cva, type VariantProps } from 'cva';
import { Loader2Icon } from 'lucide-react';
import { useTransition } from 'react';
import { Link, type LinkProps } from 'react-router';
import type { MaybePromise } from 'shared/types';

const button = cva({
  base: [
    'inline-block rounded-full border-2 text-center typography-v1-button1 transition-colors active:duration-0 disabled:cursor-default',
    'px-6 py-1.75 sm:px-10 sm:py-2.25 xl:py-3.25',
    'relative',
  ],
  variants: {
    color: {
      blue: [
        'border-v1-primary-100 bg-v1-primary-100 text-white',
        'hover:bg-white hover:text-v1-background-200',
        'active:bg-v1-background-60 active:text-v1-background-200',
        'disabled:border-[#F8F8F8] disabled:bg-[#F8F8F8] disabled:text-v1-neutral-60',
        'data-[pending=true]:border-v1-primary-100 data-[pending=true]:bg-v1-background-60 data-[pending=true]:text-v1-background-200',
      ],
      yellow: [
        'border-v1-secondary-100 bg-v1-secondary-100 text-v1-background-200',
        'hover:bg-white hover:text-v1-background-200',
        'active:bg-v1-secondary-20 active:text-v1-background-200',
        'disabled:border-v1-secondary-60 disabled:bg-v1-secondary-40 disabled:text-v1-neutral-80',
        'data-[pending=true]:border-v1-secondary-100 data-[pending=true]:bg-v1-secondary-20 data-[pending=true]:text-v1-background-200',
      ],
      white: [
        'border-v1-background-80 bg-white text-v1-background-200',
        'hover:bg-v1-background-60',
        'active:bg-v1-background-80',
        'disabled:border-v1-neutral-40 disabled:bg-white disabled:text-v1-neutral-60',
        'data-[pending=true]:border-v1-background-80 data-[pending=true]:bg-v1-background-80 data-[pending=true]:text-v1-background-200',
      ],
      red: [
        'border-v1-others-red-40 bg-v1-others-red-40 text-white',
        'hover:bg-white hover:text-v1-others-red-40',
        'active:bg-v1-others-red-10 active:text-v1-others-red-40',
        'disabled:border-v1-neutral-20 disabled:bg-v1-neutral-20 disabled:text-v1-neutral-80',
        'data-[pending=true]:border-v1-others-red-40 data-[pending=true]:bg-v1-others-red-10 data-[pending=true]:text-v1-others-red-40',
      ],
      orange: [
        'border-v1-others-orange-60 bg-v1-others-orange-60 text-white',
        'hover:bg-white hover:text-v1-background-200',
        'active:bg-v1-others-orange-10 active:text-v1-background-200',
        'disabled:border-v1-others-orange-40 disabled:bg-v1-others-orange-40 disabled:text-white',
        'data-[pending=true]:border-v1-others-orange-60 data-[pending=true]:bg-v1-others-orange-10 data-[pending=true]:text-v1-others-orange-60',
      ],
    },
  },
});

const loader = (
  <Loader2Icon
    className={clsx(
      'absolute top-1/2 left-1/2 -translate-1/2 animate-spin transition-opacity delay-50 starting:opacity-0',
    )}
  />
);

export type ButtonProps = {
  pending?: boolean;
  action?: (e: React.MouseEvent<HTMLButtonElement>) => MaybePromise<unknown>;
} & VariantProps<typeof button> &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = ({
  color = 'blue',
  children,
  className,
  pending,
  disabled,
  action,
  onClick,
  ...rest
}: ButtonProps) => {
  const [isTransitionPending, startTransition] = useTransition();
  const isPending = pending || isTransitionPending;

  return (
    <button
      data-pending={isPending}
      disabled={isPending || disabled}
      className={button({ color, className })}
      onClick={(e) => {
        if (action) {
          startTransition(async () => {
            await action(e);
          });
        }
        onClick?.(e);
      }}
      {...rest}
    >
      {isPending && loader}
      <span className={clsx('transition-opacity', isPending ? 'opacity-0 delay-0' : 'delay-50')}>
        {children}
      </span>
    </button>
  );
};

export type LinkButtonProps = VariantProps<typeof button> & LinkProps;

export const LinkButton = ({ color = 'blue', children, className, ...rest }: LinkButtonProps) => {
  return (
    <Link className={button({ color, className })} {...rest}>
      {children}
    </Link>
  );
};
